import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Shield,
  Key,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Role, Permission } from "@/types/auth";
import {
  useRoles,
  usePermissions,
  useCreateRole,
  useUpdateRole,
  useRole,
} from "@/api-services/hooks/useRoles";
import { RoleDTO } from "@/api-services/hooks/adminTypes";
import { useAdminUsers } from "@/api-services/hooks/useAdminUsers";

const AddEditRolePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const roleId = id ? parseInt(id, 10) : undefined;

  // Form state
  const [formData, setFormData] = useState({
    roleName: "",
    roleDescription: "",
    rolePermissions: "",
    permissions: [] as string[],
    restricted: [] as string[],
    remarks: "",
    status: "active",
  });

  // Validation error state
  const [roleNameError, setRoleNameError] = useState<string>("");
  const [roleDescriptionError, setRoleDescriptionError] = useState<string>("");

  // API hooks
  const rolesQuery = useRoles({
    pageable: { page: 0, size: 1000 },
  });

  const usersQuery = useAdminUsers(
  { page: 0, size: 1000 },
  {
    enabled: isEditMode, // only needed in edit mode
  }
);

const usersList = useMemo(() => {
  const data = usersQuery.data as any;
  return data?.content || [];
}, [usersQuery.data]);

  const permissionsQuery = usePermissions({});

  const { data: roleData, isLoading: isLoadingRole } = useRole(roleId || 0, {
    enabled: isEditMode && !!roleId,
  });

const normalizeRole = (role: string) => {
  return role
    ?.toLowerCase()
    .replace("role_", "")
    .trim();
};

const isRoleAssignedToAnyUser = (roleName: string): boolean => {
  const normalizedRoleName = normalizeRole(roleName);

  return usersList.some((user: any) => {
    const rolesAssigned = user.rolesAssigned;

    if (!rolesAssigned) return false;

    const userRoles = rolesAssigned
      .split(",")
      .map((r: string) => normalizeRole(r));

    // ✅ exact match
    if (userRoles.includes(normalizedRoleName)) return true;

    // ✅ partial match (handles typo like "Managerr")
    return userRoles.some((r: string) =>
      r.includes(normalizedRoleName) ||
      normalizedRoleName.includes(r)
    );
  });
};

  const createRoleMutation = useCreateRole({
    onSuccess: () => {
      toast.success("Role created successfully!");
      navigate("/admin/users/roles");
    },
    onError: (error) => {
      toast.error(`Failed to create role: ${error.message}`);
    },
  });

  const updateRoleMutation = useUpdateRole({
    onSuccess: () => {
      toast.success("Role updated successfully!");
      navigate("/admin/users/roles");
    },
    onError: (error: any) => {
      // Enhanced error handling with specific messages
      let errorMessage = "Failed to update role";
      
      if (error?.response?.status === 400) {
        errorMessage = "Invalid role data. Please check all fields and try again.";
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error?.response?.status === 409) {
        errorMessage = "Role name already exists. Please use a different name.";
      } else if (error?.response?.status === 403) {
        errorMessage = "You don't have permission to update this role.";
      } else if (error?.response?.status === 404) {
        errorMessage = "Role not found. It may have been deleted.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    },
  });

  // Convert API Role to UI Role format
  const convertApiRoleToUIRole = (apiRole: any): Role => {
    const apiRoleId = apiRole.roleId ?? apiRole.id;
    const roleIdValue = apiRoleId !== undefined && apiRoleId !== null ? String(apiRoleId) : '';

    const role: Role = {
      id: roleIdValue,
      name: apiRole.roleName || apiRole.name || '',
      description: apiRole.roleDescription || apiRole.description || '',
      isSystemRole: apiRole.isSystemRole || false,
      permissions: Array.isArray(apiRole.permissions)
        ? apiRole.permissions.map((p: any) =>
            typeof p === 'string'
              ? { id: '', name: p, resource: '', action: '', description: '' }
              : p
          )
        : [],
      createdAt: apiRole.createdAt || apiRole.createdDate || apiRole.createDate || '',
      updatedAt: apiRole.updatedAt || apiRole.modifiedDate || '',
    };

    (role as any).roleId = apiRoleId !== undefined && apiRoleId !== null 
      ? (typeof apiRoleId === 'number' ? apiRoleId : parseInt(String(apiRoleId), 10))
      : null;
    (role as any).status = (apiRole.status || '').toLowerCase();
    (role as any).rolePermissions = apiRole.rolePermissions || '';
    (role as any).restricted = Array.isArray(apiRole.restricted) ? apiRole.restricted : [];
    (role as any).isDeletedValue = apiRole.isDeletedValue || false;
    (role as any).remarks = apiRole.remarks || '';

    return role;
  };

  const roles: Role[] = rolesQuery.data?.content?.map(convertApiRoleToUIRole) || [];
  const permissions: Permission[] = permissionsQuery.data?.map((p: any) => ({
    id: String(p.id),
    name: p.name,
    resource: p.resource,
    action: p.action,
    description: p.description,
    createdAt: p.createdAt || new Date().toISOString(),
    updatedAt: p.updatedAt || new Date().toISOString(),
  })) || [];

  // Load role data for editing
  useEffect(() => {
    if (roleData && isEditMode) {
      const role = convertApiRoleToUIRole(roleData);
      const roleName = role.name || "";
      const roleDescription = role.description || "";
      const permissionNames = role.permissions.map((p) => p.name);
      const apiData = role as any;

      setFormData({
        roleName,
        roleDescription,
        rolePermissions: apiData.rolePermissions || "",
        permissions: permissionNames,
        restricted: Array.isArray(apiData.restricted) ? apiData.restricted : [],
        remarks: apiData.remarks || "",
        status: apiData.status || "",
      });
    }
  }, [roleData, isEditMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.roleName.trim()) {
      setRoleNameError("Role name is required");
      toast.error("Please fix the validation errors");
      return;
    }

    if (!formData.roleDescription.trim()) {
      setRoleDescriptionError("Role description is required");
      toast.error("Please fix the validation errors");
      return;
    }

    // Validate at least one permission is selected
    if (formData.permissions.length === 0 && formData.restricted.length === 0) {
      toast.error("Please fix the validation errors");
      return;
    }

    // Validate role name uniqueness
    const trimmedRoleName = formData.roleName.trim().toLowerCase();
    const duplicateRole = roles.find((role) => {
      const existingRoleName = (role.name || '').trim().toLowerCase();
      if (isEditMode && roleData) {
        const editingRole = convertApiRoleToUIRole(roleData);
        const editingRoleData = editingRole as any;
        const editingRoleId = editingRoleData.roleId ?? editingRole.id;
        const roleIdCheck = (role as any).roleId ?? role.id;
        return existingRoleName === trimmedRoleName && roleIdCheck !== editingRoleId;
      }
      return existingRoleName === trimmedRoleName;
    });

    if (duplicateRole) {
      setRoleNameError(`Role name "${formData.roleName}" already exists. Please use a unique name.`);
      toast.error("Please fix the validation errors");
      return;
    }

    setRoleNameError("");
    setRoleDescriptionError("");

    if (isEditMode && roleId) {
      const payload: RoleDTO = {
        roleId: roleId,
        roleName: formData.roleName,
        roleDescription: formData.roleDescription,
        rolePermissions: formData.rolePermissions || undefined,
        permissions: formData.permissions.length > 0 ? formData.permissions : undefined,
        restricted: formData.restricted.length > 0 ? formData.restricted : undefined,
        remarks: formData.remarks || undefined,
        status: formData.status ? formData.status.toUpperCase() : undefined,
      };

    // 🚨 VALIDATION: Prevent deactivating role if assigned
if (
  isEditMode &&
  roleData && // ✅ this is API roleData now
  formData.status.toLowerCase() === "inactive"
) {
  const originalRole = convertApiRoleToUIRole(roleData);
  const originalStatus = ((originalRole as any).status || "").toLowerCase();

  if (originalStatus === "active") {
    const isAssigned = isRoleAssignedToAnyUser(originalRole.name); // ✅ use ORIGINAL name

    if (isAssigned) {
      toast.error(
        `Cannot deactivate role "${originalRole.name}" because it is assigned to users.`
      );
      return;
    }
  }
}
      try {
        await updateRoleMutation.mutate({
          id: roleId,
          ...payload,
        });
      } catch (error: any) {
        // Error is already handled in the mutation's onError
        console.error('Role update error:', error);
      }
    } else {
      const roleData: RoleDTO = {
        roleName: formData.roleName,
        roleDescription: formData.roleDescription,
        rolePermissions: formData.rolePermissions || undefined,
        permissions: formData.permissions.length > 0 ? formData.permissions : undefined,
        restricted: formData.restricted.length > 0 ? formData.restricted : undefined,
        remarks: formData.remarks || undefined,
        status: formData.status ? formData.status.toUpperCase() : undefined,
      };

      await createRoleMutation.mutate(roleData);
    }
  };

  const handlePermissionChange = (permission: Permission, value: string) => {
    setFormData((prev) => {
      const newPermissions = prev.permissions.filter((name) => name !== permission.name);
      const newRestricted = prev.restricted.filter((name) => name !== permission.name);
      if (value === "grant") {
        newPermissions.push(permission.name);
      } else if (value === "restrict") {
        newRestricted.push(permission.name);
      }
      return { ...prev, permissions: newPermissions, restricted: newRestricted };
    });
  };

  const handleSectionToggle = (resourcePermissions: Permission[], value: string) => {
    setFormData((prev) => {
      const names = resourcePermissions.map((p) => p.name);
      const newPermissions = prev.permissions.filter((name) => !names.includes(name));
      const newRestricted = prev.restricted.filter((name) => !names.includes(name));
      if (value === "grant") {
        newPermissions.push(...names);
      } else if (value === "restrict") {
        newRestricted.push(...names);
      }
      return { ...prev, permissions: newPermissions, restricted: newRestricted };
    });
  };

  const getPermissionsByResource = useCallback(() => {
    const grouped = permissions.reduce((acc, permission) => {
      if (!acc[permission.resource]) {
        acc[permission.resource] = [];
      }
      acc[permission.resource].push(permission);
      return acc;
    }, {} as Record<string, Permission[]>);

    return Object.entries(grouped);
  }, [permissions]);

  const getPermissionIcon = (action: string) => {
    return <Key className="h-4 w-4" />;
  };

  if (isLoadingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading role...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/users/roles")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {isEditMode ? "Edit Role" : "Add New Role"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEditMode
                ? "Update the role information and permissions below."
                : "Create a new role with specific permissions."}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/users/roles")}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              createRoleMutation.loading ||
              updateRoleMutation.loading || 
              !!roleNameError
            }
          >
            <Save className="w-4 h-4 mr-2" />
            {createRoleMutation.loading || updateRoleMutation.loading
              ? "Saving..."
              : isEditMode
              ? "Update Role"
              : "Create Role"}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Enter the role name and description</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Role Name <span className="text-red-500">*</span></label>
                <Input
                  value={formData.roleName}
                  onChange={(e) => {
                    const newRoleName = e.target.value;
                    setFormData((prev) => ({ ...prev, roleName: newRoleName }));
                    
                    if (roleNameError) {
                      setRoleNameError("");
                    }

                    if (newRoleName.trim()) {
                      const trimmedRoleName = newRoleName.trim().toLowerCase();
                      const duplicateRole = roles.find((role) => {
                        const existingRoleName = (role.name || '').trim().toLowerCase();
                        if (isEditMode && roleData) {
                          const editingRole = convertApiRoleToUIRole(roleData);
                          const editingRoleData = editingRole as any;
                          const editingRoleId = editingRoleData.roleId ?? editingRole.id;
                          const roleIdCheck = (role as any).roleId ?? role.id;
                          return existingRoleName === trimmedRoleName && roleIdCheck !== editingRoleId;
                        }
                        return existingRoleName === trimmedRoleName;
                      });

                      if (duplicateRole) {
                        setRoleNameError(`Role name "${newRoleName.trim()}" already exists.`);
                      }
                    }
                  }}
                  placeholder="Driver Manager"
                  required
                  className={roleNameError ? "border-red-500 focus-visible:ring-red-500" : ""}
                />
                {roleNameError && (
                  <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <span className="text-red-500">⚠</span>
                    {roleNameError}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, status: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Role Description <span className="text-red-500">*</span></label>
              <textarea
                value={formData.roleDescription}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, roleDescription: e.target.value }));
                  if (roleDescriptionError) setRoleDescriptionError("");
                }}
                placeholder="Describe the role and its responsibilities..."
                className={`mt-1 min-h-[100px] w-full px-3 py-2 border rounded-md bg-background ${roleDescriptionError ? "border-red-500 focus-visible:ring-red-500" : "border-border"}`}
                required
              />
              {roleDescriptionError && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                  <span className="text-red-500">⚠</span>
                  {roleDescriptionError}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Permissions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Permissions
            </CardTitle>
            <CardDescription>
              For each permission, choose to <span className="text-green-600 font-medium">Grant</span> (allow) or <span className="text-red-500 font-medium">Restrict</span> (deny) access. Only one can be selected per permission.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <Accordion type="multiple" defaultValue={[]} className="space-y-2">
              {getPermissionsByResource()
                .filter(([resource]) => resource !== "referrals")
                .map(([resource, resourcePermissions]) => {
                  const grantedCount = resourcePermissions.filter(p => formData.permissions.includes(p.name)).length;
                  const restrictedCount = resourcePermissions.filter(p => formData.restricted.includes(p.name)).length;
                  const allGranted = grantedCount === resourcePermissions.length;
                  const allRestricted = restrictedCount === resourcePermissions.length;
                  const sectionValue = allGranted ? "grant" : allRestricted ? "restrict" : "";
                  return (
                    <AccordionItem key={resource} value={resource} className="border rounded-lg overflow-hidden px-0">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30 [&[data-state=open]]:bg-muted/50">
                        <div className="flex items-center gap-2 flex-1">
                          <Shield className="h-4 w-4 text-primary shrink-0" />
                          <span className="font-medium">{resource.charAt(0).toUpperCase() + resource.slice(1)}</span>
                          <div className="flex items-center gap-3 ml-auto mr-2" onClick={(e) => e.stopPropagation()}>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="radio"
                                name={`section-${resource}`}
                                checked={allGranted}
                                onChange={() => handleSectionToggle(resourcePermissions, "grant")}
                                className="h-4 w-4 accent-green-600"
                              />
                              <span className={`text-xs font-medium ${allGranted ? 'text-green-600' : 'text-muted-foreground'}`}>Grant All</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="radio"
                                name={`section-${resource}`}
                                checked={allRestricted}
                                onChange={() => handleSectionToggle(resourcePermissions, "restrict")}
                                className="h-4 w-4 accent-red-500"
                              />
                              <span className={`text-xs font-medium ${allRestricted ? 'text-red-500' : 'text-muted-foreground'}`}>Restrict All</span>
                            </label>
                            {grantedCount > 0 && !allGranted && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{grantedCount} granted</span>
                            )}
                            {restrictedCount > 0 && !allRestricted && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">{restrictedCount} restricted</span>
                            )}
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pb-0">
                        <div className="divide-y">
                          {resourcePermissions.map((permission) => {
                            const isGranted = formData.permissions.includes(permission.name);
                            const isRestricted = formData.restricted.includes(permission.name);
                            const radioValue = isGranted ? "grant" : isRestricted ? "restrict" : "none";
                            return (
                              <div
                                key={permission.id}
                                className="flex items-center px-4 py-2.5 gap-4 hover:bg-muted/30"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">{permission.name}</div>
                                  <div className="text-xs text-muted-foreground truncate">{permission.description}</div>
                                </div>
                                <div className="flex items-center gap-4 shrink-0">
                                  <label className="flex items-center gap-1.5 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`perm-${permission.id}`}
                                      checked={isGranted}
                                      onChange={() => handlePermissionChange(permission, "grant")}
                                      className="h-4 w-4 accent-green-600"
                                    />
                                    <span className={`text-xs font-medium ${isGranted ? 'text-green-600' : 'text-muted-foreground'}`}>Grant</span>
                                  </label>
                                  <label className="flex items-center gap-1.5 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`perm-${permission.id}`}
                                      checked={isRestricted}
                                      onChange={() => handlePermissionChange(permission, "restrict")}
                                      className="h-4 w-4 accent-red-500"
                                    />
                                    <span className={`text-xs font-medium ${isRestricted ? 'text-red-500' : 'text-muted-foreground'}`}>Restrict</span>
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                }
              )}
            </Accordion>
          </CardContent>
        </Card>

        {/* Additional Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">Remarks</label>
              <textarea
                value={formData.remarks}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    remarks: e.target.value,
                  }))
                }
                placeholder="Additional remarks or notes..."
                className="mt-1 min-h-[80px] w-full px-3 py-2 border border-border rounded-md bg-background"
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/users/roles")}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              createRoleMutation.loading ||
              updateRoleMutation.loading || 
              !!roleNameError
            }
          >
            <Save className="w-4 h-4 mr-2" />
            {createRoleMutation.loading || updateRoleMutation.loading
              ? "Saving..."
              : isEditMode
              ? "Update Role"
              : "Create Role"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddEditRolePage;

